// packages/shared/src/roleforge/methodology/opportunity-matrix.ts
// Plan 3b-i + 3e-i: 2×2 Opportunity Matrix. Axes: Business Impact × Ease of Implementation.
// Effort tags (Easy/Medium/Hard) are independent of quadrant. Candidate classes
// (standard | foundation | gap) carry distinct visual treatments — see
// visualTreatmentForCandidate.

import type {
  Quadrant,
  QuadrantDescriptor,
  EffortTag,
  FunnelType,
  CandidateClass,
  OpportunityVisualTreatment,
} from './types';

export const QUADRANTS: QuadrantDescriptor[] = [
  {
    id: 'quick_wins',
    display_name: 'Quick Wins',
    description: '1st priority — start here for immediate ROI.',
    position: 'top_left',
  },
  {
    id: 'nice_to_haves',
    display_name: 'Nice-to-haves',
    description: 'Sweeteners, not main focus.',
    position: 'top_right',
  },
  {
    id: 'big_swings',
    display_name: 'Big Swings',
    description: 'Next phase — built on Quick Wins foundation.',
    position: 'bottom_left',
  },
  {
    id: 'deprioritize',
    display_name: 'Deprioritize',
    description: 'Avoid — money pits.',
    position: 'bottom_right',
  },
];

export const EFFORT_TAGS: EffortTag[] = ['easy', 'medium', 'hard'];

export const FUNNEL_TYPES: FunnelType[] = [
  'lto', 'lead_capture', 'sales_call', 'website', 'hybrid', 'none',
];

/**
 * Resolve quadrant from numeric scores (0..10 each).
 * Threshold: 5 on each axis splits high/low.
 */
export function quadrantFromAxes(business_impact: number, ease_score: number): Quadrant {
  const high_impact = business_impact >= 5;
  const easy = ease_score >= 5;
  if (high_impact && easy) return 'quick_wins';
  if (high_impact && !easy) return 'big_swings';
  if (!high_impact && easy) return 'nice_to_haves';
  return 'deprioritize';
}

/**
 * Funnel-agnostic enforcement helper — Plan 3b-ii's synthesizer uses this to
 * verify the recommendation set spans multiple funnel types. Returns the unique
 * funnel types used; if length === 1 AND that type is 'lto', the synthesizer
 * MUST diversify (per strategy doc §8 audience-and-positioning constraint).
 */
export function uniqueFunnelTypes(funnel_types: Array<FunnelType | null | undefined>): FunnelType[] {
  return Array.from(new Set(funnel_types.filter((f): f is FunnelType => Boolean(f))));
}

export function isFunnelAgnostic(funnel_types: Array<FunnelType | null | undefined>): boolean {
  const unique = uniqueFunnelTypes(funnel_types);
  if (unique.length >= 2) return true;
  if (unique.length === 1 && unique[0] === 'none') return true;
  // Single LTO recommendation set → not funnel-agnostic. Synthesizer must diversify.
  return false;
}

// ─── Candidate-class helpers (Plan 3e-i) ────────────────────────────────

/** Visual treatment per candidate class. */
export function visualTreatmentForCandidate(c: CandidateClass): OpportunityVisualTreatment {
  switch (c) {
    case 'foundation': return { border: 'dashed', chip: 'foundation' };
    case 'gap':        return { border: 'solid',  chip: 'multi-source' };
    case 'standard':   return { border: 'solid',  chip: null };
  }
}

/** Per spec §11.5, gap items auto-promote to quick_wins regardless of axes. */
export function gapAutoPromotionQuadrant(_business_impact: number, _ease_score: number): Quadrant {
  return 'quick_wins';
}

export function candidateClassFromMetadata(metadata: unknown): CandidateClass {
  const c = (metadata as { candidate_class?: unknown } | null)?.candidate_class;
  if (c === 'foundation' || c === 'gap') return c;
  return 'standard';
}

/** 4-question task-suitability filter. 3-of-4 passes. */
export function passesTaskSuitability(scores: {
  structured_input: boolean;
  rule_based: boolean;
  predictable_output: boolean;
  repeated_often: boolean;
}): boolean {
  let count = 0;
  if (scores.structured_input) count++;
  if (scores.rule_based) count++;
  if (scores.predictable_output) count++;
  if (scores.repeated_often) count++;
  return count >= 3;
}
